import AddCircleOutlined from '@mui/icons-material/AddCircleOutlined'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { IconButton, Tooltip } from '@mui/material'
import React, { useRef } from 'react'
import { EVENT_TYPE_CONSULTATION } from 'src/consumer/constants'
import { getEmrApiData } from 'src/shared/utils/calendarReportApiHelper'
import MoreOptionsForBookedSlotStrip from 'src/clinic/components/ClinicManageBookingsPage/slotItem/components/MoreOptionsForBookedSlotStrip'
import { toast } from 'react-toastify'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { useMultipleDialogs } from 'src/clinic/components/ClinicManageBookingsPage/slotItem/hooks/useDialogState'
import { usePrescriptionDialogs } from 'src/clinic/components/ClinicManageBookingsPage/slotItem/hooks/usePrescriptionDialogs'
import { usePrescriptionDownload } from 'src/clinic/components/ClinicManageBookingsPage/slotItem/hooks/usePrescriptionDownload'
import BookedSlotDialogs from 'src/clinic/components/ClinicManageBookingsPage/slotItem/components/BookedSlotDialogs'
import {
  calculateCreatedTimeFrom,
  calculateCreatedTimeTo,
} from 'src/clinic/components/ClinicManageBookingsPage/slotItem/utils/timeCalculations'

export default function ConsultsActions({
  bookedSlot,
  mentorProfileData,
  clinicLocationData,
  handleNewTherapyRequest,
  setClickedBookedSlotData,
  therapyBookingDataForThisEvent,
  setEditPatientDialogOpen,
  forMentor,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  refreshBookedSlots,
  showPrescriptionPdfDownloadButtons,
  sendPrescriptionPdfApiSignature,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
}) {
  const menuRef = useRef(null)

  const dialogs = useMultipleDialogs([
    'sendTherapy',
    'paymentLinks',
    'joiningDetails',
    'bookedEventLogs',
    'uploadPrescriptionImages',
    'sendAssessment',
    'patientBooking',
    'emrImages',
    'reschedule',
  ])
  const prescriptionDownload = usePrescriptionDownload({
    bookedSlot,
    sendPrescriptionPdfApiSignature,
    refreshBookedSlots,
  })

  const prescriptionDialogs = usePrescriptionDialogs(prescriptionDownload)

  const openMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    menuRef.current.openMenuOnAnchor(e)
  }

  const openSendTherapyPopover = () => {
    dialogs.sendTherapy.open()
  }
  const handleToggleRecording = async () => {
    const loadingToast = toast.loading('Updating Recording Status...')
    const apiUrl = !bookedSlot?.recordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-recordable'
      : '/api/secure/cc/support/events/mark-event-as-not-recordable'

    const resp = await putSPDataWithParams(apiUrl, null, {
      'booked-event-id': bookedSlot?.id,
    })

    if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      refreshBookedSlots()
      toast.update(loadingToast, {
        render: 'Recording Status Updated',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
    } else {
      toast.update(loadingToast, {
        render: 'Failed to update recording status',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  const isConsultation = bookedSlot?.eventType === EVENT_TYPE_CONSULTATION
  const emrApiData = getEmrApiData(bookedSlot?.eventType)
  // Upload is available for every session type: consultations attach a prescription, all other
  // report types (therapy / screening / assessment / investigation) attach report images via the
  // EMR dialog. Opens the appropriate dialog for the booking's event type.
  const canUpload = forMentor && (isConsultation || Boolean(emrApiData?.uploadImagesSignature))
  const openUploadForThisEvent = () => {
    if (isConsultation) {
      dialogs.uploadPrescriptionImages.open()
    } else {
      dialogs.emrImages.open()
    }
  }

  return (
    <>
      {canUpload && (
        <Tooltip title={isConsultation ? 'Upload prescription' : 'Upload report'} arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              openUploadForThisEvent()
            }}
            sx={{
              color: '#d97706',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(245, 158, 11, 0.14)',
                color: '#b45309',
              },
            }}
          >
            <UploadFileRoundedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="More options" arrow>
        <IconButton
          size="small"
          onClick={openMenu}
          sx={{
            color: '#6366F1',
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(99, 102, 241, 0.12)',
              color: '#4338CA',
            },
          }}
        >
          <AddCircleOutlined sx={{ fontSize: 26 }} />
        </IconButton>
      </Tooltip>
      <MoreOptionsForBookedSlotStrip
        handleNewTherapyRequest={handleNewTherapyRequest}
        therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
        bookedSlot={bookedSlot}
        setClickedBookedSlotData={setClickedBookedSlotData}
        setEditPatientDialogOpen={setEditPatientDialogOpen}
        forMentor={forMentor}
        setAddVitalsDialogOpen={setAddVitalsDialogOpen}
        setEditBookingDialogOpen={setEditBookingDialogOpen}
        refreshBookedEvents={refreshBookedSlots}
        openSendTherapyPopover={openSendTherapyPopover}
        handleOpenJoiningDetails={dialogs.joiningDetails.open}
        openBookedEventLogs={dialogs.bookedEventLogs.open}
        openUploadPrescriptionImagesDialog={dialogs.uploadPrescriptionImages.open}
        openEmrImagesDialog={dialogs.emrImages.open}
        openSendAssessmentDialog={dialogs.sendAssessment.open}
        openPatientBookingDialog={dialogs.patientBooking.open}
        handleToggleRecording={handleToggleRecording}
        showPrescriptionPdfDownloadButtons={showPrescriptionPdfDownloadButtons}
        openPrescriptionPdfDownloadDialog={prescriptionDialogs.openOptionsDialog}
        ref={menuRef}
      />

      <BookedSlotDialogs
        dialogs={dialogs}
        bookedSlot={bookedSlot}
        mentorProfileData={mentorProfileData}
        clinicLocationData={clinicLocationData}
        prescriptionGetApiSignature={prescriptionGetApiSignature}
        prescriptionSaveApiSignature={prescriptionSaveApiSignature}
        fileUploadApiSignature={fileUploadApiSignature}
        showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
        sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
        sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
        prescriptionImagesGetApiSignature={prescriptionImagesGetApiSignature}
        prescriptionImagesSaveApiSignature={prescriptionImagesSaveApiSignature}
        emrImagesGetApiSignature={emrApiData?.getApiSignature}
        emrImagesSaveApiSignature={emrApiData?.saveApiSignature}
        emrImagesUploadImagesSignature={emrApiData?.uploadImagesSignature}
        refreshBookedSlots={refreshBookedSlots}
        prescriptionDialogs={prescriptionDialogs}
        calculateCreatedTimeFrom={calculateCreatedTimeFrom}
        calculateCreatedTimeTo={calculateCreatedTimeTo}
      />
    </>
  )
}
